import { sendTokenToExtension } from "@/helpers/send-to-extension.ts";
import useErrorHandle from "@/hooks/use-error-handle.hook.tsx";
import { ServerErrorField } from "@/components/server-error-field/ServerErrorField.tsx";
import { useTitle } from "@/hooks/use-title.hook.ts";
import { FullScreenCard } from "@/pages/security/components/FullScreenCard.tsx";
import { TwoFactorVerifyForm } from "@/pages/security/components/TwoFactorVerifyForm.tsx";
import { getAuthorizationFlow } from "@/pages/security/authorization.helper.ts";
import {
  useEmployeeChangePassword,
  useEmployeeMeInvalidate,
  useEmployeeResetPassword,
} from "@/queries/employees/use-employees.query.ts";
import analyticsService, { analyticEvents } from "@/services/analytics-service.ts";
import { useAuthStore } from "@/stores/employee.store.ts";
import { ApiErrorException } from "@/types/api.types.ts";
import { ApiAuthorization, ChangePasswordSource } from "@/types/employees/employee.types.ts";
import { zodResolver } from "@hookform/resolvers/zod";
import { Box, Button, Input, Stack, Text, Title } from "@skailer-com/uikit";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

import { z } from "zod";

export default function ResetPassword() {
  const { t } = useTranslation();
  const { token } = useParams();
  const { handleErrors } = useErrorHandle();
  useTitle(t("core:reset_password"));

  const { setToken } = useAuthStore();

  const ResetPasswordSchemaObject = z.object({
    password: z
      .string({ error: t("errors:password_required") })
      .min(1, t("errors:password_required"))
      .min(8, t("api_errors:minimum_value_must", { minimum: 8 }))
      .regex(/[a-z]/, t("api_errors:password_must_contain_lowercase"))
      .regex(/[A-Z]/, t("api_errors:password_must_contain_uppercase"))
      .regex(/[0-9]/, t("api_errors:password_must_contain_number"))
      .regex(/[!@#$%^&*(),.?":{}|<>]/, t("api_errors:password_must_contain_special")),
  });

  type ResetPasswordSchema = z.infer<typeof ResetPasswordSchemaObject>;

  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isSubmitting },
  } = useForm<ResetPasswordSchema>({
    mode: "onSubmit",
    resolver: zodResolver(ResetPasswordSchemaObject),
  });

  const employeeMeInvalidate = useEmployeeMeInvalidate();
  const changePasswordMutation = useEmployeeChangePassword(control);
  const resetPasswordMutation = useEmployeeResetPassword();
  const [pendingPassword, setPendingPassword] = useState<string>();
  const [tfaAccessToken, setTfaAccessToken] = useState<string>();
  const [tfaStep, setTfaStep] = useState<"before_password" | "after_password">();
  const [isRedirecting, setIsRedirecting] = useState(false);

  const completeResetPassword = (accessToken: string) => {
    setToken(accessToken);
    sendTokenToExtension(accessToken);
    employeeMeInvalidate();
    analyticsService.trackEvent(analyticEvents.passwordReset);
    setIsRedirecting(true);
    navigate("/");
  };

  const changePassword = async (password: string, accessToken: string) => {
    try {
      const data = await changePasswordMutation.mutateAsync({
        password,
        source: ChangePasswordSource.resetPassword,
        accessToken,
      });

      const authorizationFlow = getAuthorizationFlow(data);
      if (authorizationFlow === "tfa_code") {
        setTfaStep("after_password");
        setTfaAccessToken(data.access_token);
        return true;
      }

      if (authorizationFlow === "tfa_setup") {
        setIsRedirecting(true);
        navigate(`/tfa-setup/${data.access_token}`);
        return true;
      }

      completeResetPassword(data.access_token);
      return true;
    } catch {
      return false;
    }
  };

  const handleAuthorization = async (authorization: ApiAuthorization, password: string) => {
    const authorizationFlow = getAuthorizationFlow(authorization);

    if (authorizationFlow === "tfa_code") {
      setPendingPassword(password);
      setTfaStep("before_password");
      setTfaAccessToken(authorization.access_token);
      return;
    }

    if (authorizationFlow === "tfa_setup") {
      navigate(`/tfa-setup/${authorization.access_token}`);
      return;
    }

    await changePassword(password, authorization.access_token);
  };

  const onSubmit = handleSubmit(async (loginData) => {
    if (!token) return;

    try {
      const data = await resetPasswordMutation.mutateAsync(token);
      await handleAuthorization(data, loginData.password);
    } catch (error) {
      handleErrors(error instanceof ApiErrorException ? error.apiError : undefined, control);
    }
  });

  const handleTfaVerifySuccess = async (accessToken: string) => {
    if (tfaStep === "after_password") {
      completeResetPassword(accessToken);
      return;
    }

    if (!pendingPassword) return;

    const isPasswordChanged = await changePassword(pendingPassword, accessToken);
    if (!isPasswordChanged) setTfaAccessToken(undefined);
  };

  const isLoading =
    isSubmitting ||
    resetPasswordMutation.isPending ||
    changePasswordMutation.isPending ||
    isRedirecting;

  return (
    <FullScreenCard>
      <Stack gap={"sm"} className={"w-full sm:max-w-md "}>
        {tfaAccessToken ? (
          <TwoFactorVerifyForm
            key={tfaAccessToken}
            accessToken={tfaAccessToken}
            onSuccess={handleTfaVerifySuccess}
          />
        ) : isRedirecting ? (
          <Box className="w-full p-6 lg:p-8 bg-light rounded-xs shadow-md">
            <Button size="lg" className="w-full" isLoading disabled>
              {t("common:loading")}
            </Button>
          </Box>
        ) : (
          <Box className="w-full p-6 lg:p-8 bg-light rounded-xs shadow-md">
            <Stack gap={"2xl"}>
              <a
                href={import.meta.env.VITE_LANDING_URL + "/"}
                className="flex items-center mb-6 text-2xl font-semibold gap-x-[6px]"
              >
                <img src="/logo.png" alt="Skailer" className="h-[28px] w-auto" />
                <Text className={"text-[22px] leading-[24px]"}>{t("core:app_name")}</Text>
              </a>
              <Stack gap={"lg"}>
                <Title header={t("core:reset_password")} size={"lg"} />
                <form onSubmit={onSubmit}>
                  <Input
                    label={t("common:password")}
                    type="password"
                    {...register("password")}
                    placeholder="••••••••"
                    error={errors.password?.message}
                  />
                </form>
                <ServerErrorField errors={errors} />
                <Button
                  type={"submit"}
                  size="lg"
                  onClick={onSubmit}
                  className="w-full"
                  isLoading={isLoading}
                  disabled={isLoading}
                >
                  {t("common:reset")}
                </Button>
              </Stack>
            </Stack>
          </Box>
        )}
      </Stack>
    </FullScreenCard>
  );
}
